import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  DocumentPlusIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  StarIcon as StarOutline,
  PencilSquareIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'
import { StarIcon as StarSolid } from '@heroicons/react/24/solid'
import type { Resume, ResumeAnalysis, SubscriptionPlan } from '../../types'
import { canUseFeature } from '../../utils/subscription'
import { UpgradePrompt } from '../UpgradePrompt'
import { PageLayout } from '../PageLayout'
import { Skeleton } from '../Skeleton'
import {
  listResumes,
  createResume,
  deleteResume,
  duplicateResume,
  setMaster,
  importResumeFromAnalysis,
  tailorResumeForApplication,
} from '../../services/resumeBuilderRepo'
import { fetchLatestResume } from '../../services/resumeService'
import { createBlankResume } from '../../utils/resumeBuilder'
import { downloadResumePdf } from '../../utils/resumePdf'
import { trackGAResumeCreated, trackGAResumeDownloaded, trackGAResumeTailored } from '../../utils/googleAnalytics'
import { trackResumeCreated, trackResumeDownloaded, trackResumeTailored } from '../../utils/facebookPixel'

interface Props {
  userId: string
  plan: SubscriptionPlan
  email: string | null
  displayName: string | null
}

export function ResumeBuilderListPage({ userId, plan, email, displayName }: Props) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const tailorAppId = searchParams.get('tailor')
  const [resumes, setResumes] = useState<Resume[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [latestUpload, setLatestUpload] = useState<ResumeAnalysis | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<Resume | null>(null)

  const isPro = canUseFeature(plan, 'resume_builder')

  useEffect(() => {
    if (!userId || !isPro) {
      setLoading(false)
      return
    }
    let cancelled = false
    Promise.all([listResumes(userId), fetchLatestResume(userId).catch(() => null)])
      .then(([list, latest]) => {
        if (cancelled) return
        setResumes(list)
        setLatestUpload(latest)
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : 'Failed to load resumes'))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [userId, isPro])

  const handleNewBlank = async () => {
    setIsCreating(true)
    try {
      const seed = createBlankResume({ displayName, email })
      const created = await createResume({ userId, ...seed })
      trackGAResumeCreated('blank', created.template)
      trackResumeCreated('blank', created.template)
      navigate(`/resume-builder/${created.id}`)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create resume')
    } finally {
      setIsCreating(false)
    }
  }

  const handleImportFromUpload = async () => {
    if (!latestUpload) return
    setIsImporting(true)
    setError(null)
    try {
      const { resumeId } = await importResumeFromAnalysis(latestUpload.id)
      trackGAResumeCreated('import_analysis')
      trackResumeCreated('import_analysis')
      navigate(`/resume-builder/${resumeId}`)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import failed')
    } finally {
      setIsImporting(false)
    }
  }

  const handleDuplicate = async (resume: Resume) => {
    setBusyId(resume.id)
    try {
      const dup = await duplicateResume(resume.id, userId)
      trackGAResumeCreated('duplicate', dup.template)
      trackResumeCreated('duplicate', dup.template)
      setResumes((prev) => [dup, ...prev])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Duplicate failed')
    } finally {
      setBusyId(null)
    }
  }

  const handleSetMaster = async (resume: Resume) => {
    setBusyId(resume.id)
    try {
      await setMaster(resume.id, userId)
      setResumes((prev) => prev.map((r) => ({ ...r, isMaster: r.id === resume.id })))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to set master')
    } finally {
      setBusyId(null)
    }
  }

  const handleDelete = async (resume: Resume) => {
    setBusyId(resume.id)
    try {
      await deleteResume(resume.id, userId)
      setResumes((prev) => prev.filter((r) => r.id !== resume.id))
      setConfirmDelete(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed')
    } finally {
      setBusyId(null)
    }
  }

  const handleDownload = async (resume: Resume) => {
    setBusyId(resume.id)
    try {
      await downloadResumePdf(resume)
      trackGAResumeDownloaded(resume.template, resume.contact.font)
      trackResumeDownloaded(resume.template, resume.contact.font)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'PDF generation failed')
    } finally {
      setBusyId(null)
    }
  }

  const handleTailorWith = async (resume: Resume) => {
    if (!tailorAppId) return
    setBusyId(resume.id)
    setError(null)
    try {
      const { resumeId } = await tailorResumeForApplication(resume.id, tailorAppId)
      trackGAResumeTailored(tailorAppId)
      trackResumeTailored(tailorAppId)
      trackGAResumeCreated('tailor')
      trackResumeCreated('tailor')
      navigate(`/resume-builder/${resumeId}`)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Tailoring failed')
    } finally {
      setBusyId(null)
    }
  }

  const sortedResumes = useMemo(() => resumes, [resumes])

  if (!isPro) {
    return (
      <PageLayout icon={DocumentPlusIcon} title="Resume Builder" subtitle="Build and tailor resumes, export to PDF">
        <UpgradePrompt type="feature" featureName="Resume Builder" />
      </PageLayout>
    )
  }

  return (
    <PageLayout icon={DocumentPlusIcon} title="Resume Builder" subtitle="Build, tailor, and export resumes as PDF">
      {error && (
        <div className="mb-4 rounded-lg border border-rose-300 dark:border-rose-900/40 bg-rose-50 dark:bg-rose-950/40 px-3 py-2 text-sm text-rose-700 dark:text-rose-100">
          {error}
        </div>
      )}

      {tailorAppId && (
        <div className="mb-4 rounded-lg border border-sky-300 dark:border-sky-900/40 bg-sky-50 dark:bg-sky-950/30 px-3 py-2 text-sm text-sky-800 dark:text-sky-200 flex items-center gap-2">
          <SparklesIcon className="h-4 w-4" />
          <span>Pick a resume to tailor for the selected application. A new tailored copy will be created.</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          onClick={handleNewBlank}
          disabled={isCreating || isImporting}
          className="inline-flex items-center gap-1.5 rounded-lg bg-app-primary px-3 py-2 text-sm font-semibold text-white hover:bg-app-primary-hover disabled:opacity-50 transition-colors"
        >
          {isCreating ? (
            <>
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              Creating…
            </>
          ) : (
            <>
              <DocumentPlusIcon className="h-4 w-4" />
              New blank resume
            </>
          )}
        </button>
        {latestUpload && (
          <button
            onClick={handleImportFromUpload}
            disabled={isImporting || isCreating}
            className="inline-flex items-center gap-1.5 rounded-lg border border-app-accent px-3 py-2 text-sm font-semibold text-app-accent hover:bg-app-highlight disabled:opacity-50 transition-colors"
          >
            {isImporting ? (
              <>
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-sky-500 border-t-transparent" />
                Importing…
              </>
            ) : (
              <>
                <ArrowDownTrayIcon className="h-4 w-4" />
                Import from uploaded resume
              </>
            )}
          </button>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      ) : sortedResumes.length === 0 ? (
        <div className="rounded-lg border border-dashed border-app-line px-6 py-10 text-center">
          <DocumentPlusIcon className="h-10 w-10 mx-auto text-app-muted mb-2" />
          <p className="text-sm text-app-muted font-medium">No resumes yet</p>
          <p className="text-xs text-app-muted mt-1">Create your first resume to get started.</p>
        </div>
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedResumes.map((r) => (
            <li
              key={r.id}
              className="rounded-lg border border-app-line bg-app-surface p-4 flex flex-col gap-3"
            >
              <div className="flex items-start justify-between gap-2">
                <button
                  onClick={() => navigate(`/resume-builder/${r.id}`)}
                  className="text-left flex-1 min-w-0"
                >
                  <div className="flex items-center gap-1.5">
                    {r.isMaster && <StarSolid className="h-4 w-4 text-amber-500 flex-shrink-0" />}
                    <p className="font-semibold text-app-ink truncate">{r.title}</p>
                  </div>
                  <p className="text-xs text-app-muted mt-0.5">
                    {r.template} · Updated {new Date(r.updatedAt).toLocaleDateString()}
                  </p>
                  {r.tailoredForApplicationId && (
                    <p className="text-[11px] text-sky-600 dark:text-sky-400 mt-0.5">Tailored for a job</p>
                  )}
                </button>
              </div>

              <div className="flex flex-wrap gap-1.5">
                {tailorAppId && (
                  <ActionBtn onClick={() => handleTailorWith(r)} label="Tailor with AI" disabled={busyId === r.id} variant="primary">
                    <SparklesIcon className="h-3.5 w-3.5" /> Tailor
                  </ActionBtn>
                )}
                <ActionBtn onClick={() => navigate(`/resume-builder/${r.id}`)} label="Edit">
                  <PencilSquareIcon className="h-3.5 w-3.5" /> Edit
                </ActionBtn>
                <ActionBtn onClick={() => handleDownload(r)} label="Download PDF" disabled={busyId === r.id}>
                  <ArrowDownTrayIcon className="h-3.5 w-3.5" /> PDF
                </ActionBtn>
                <ActionBtn onClick={() => handleDuplicate(r)} label="Duplicate" disabled={busyId === r.id}>
                  <DocumentDuplicateIcon className="h-3.5 w-3.5" /> Copy
                </ActionBtn>
                {!r.isMaster && (
                  <ActionBtn onClick={() => handleSetMaster(r)} label="Set as master" disabled={busyId === r.id}>
                    <StarOutline className="h-3.5 w-3.5" /> Master
                  </ActionBtn>
                )}
                <ActionBtn onClick={() => setConfirmDelete(r)} label="Delete" variant="danger" disabled={busyId === r.id}>
                  <TrashIcon className="h-3.5 w-3.5" /> Delete
                </ActionBtn>
              </div>
            </li>
          ))}
        </ul>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setConfirmDelete(null)}>
          <div className="bg-app-surface max-w-md w-full p-5 rounded-2xl shadow-app-card" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-app-ink font-display">Delete this resume?</h3>
            <p className="text-sm text-app-muted mt-1">
              "{confirmDelete.title}" will be permanently deleted. This cannot be undone.
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setConfirmDelete(null)} className="px-3 py-1.5 text-sm font-medium text-app-muted hover:bg-app-subtle rounded-lg">Cancel</button>
              <button onClick={() => handleDelete(confirmDelete)} disabled={busyId === confirmDelete.id} className="bg-rose-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50 rounded-lg">Delete</button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  )
}

function ActionBtn({ onClick, label, children, disabled, variant = 'ghost' }: { onClick: () => void; label: string; children: React.ReactNode; disabled?: boolean; variant?: 'ghost' | 'danger' | 'primary' }) {
  const cls = variant === 'danger'
    ? 'text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20'
    : variant === 'primary'
      ? 'bg-app-primary text-white hover:bg-app-primary-hover'
      : 'text-app-muted hover:bg-app-subtle'
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium disabled:opacity-50 transition-colors ${cls}`}
    >
      {children}
    </button>
  )
}
